<script setup lang="ts">
import { ref } from 'vue';

import {
  PriceCalendar,
  PriceCalendarSingleMonthRibbon,
  PriceCalendarTileType,
  MonthsSelector,
} from '@apps/main/new-desktop/modules/calendars';

import { AppFlicker } from '@libs/ui';

import type {
  PeekPriceModalCalendar,
  PeekPriceModalCustomItinerary,
} from '../../models';

import PeekPriceModalMobileLayout from './PeekPriceModalMobileLayout.vue';
import PeekPriceModalItinerary from './PeekPriceModalItinerary.vue';

interface PeekPriceModalMobileProps {
  calendar: PeekPriceModalCalendar;
  itinerary: PeekPriceModalCustomItinerary;
}

const props = defineProps<PeekPriceModalMobileProps>();

interface PeekPriceModalEmits {
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
  (eventName: 'select-date', date: Date): void;
  (eventName: 'select-month', date: Date): void;
}

const emit = defineEmits<PeekPriceModalEmits>();

const shouldShowMonthsSelector = ref<boolean>(false);

function onLayotShift(): void {
  shouldShowMonthsSelector.value = false;
}

function onToggleMonthsSelector(): void {
  shouldShowMonthsSelector.value = !shouldShowMonthsSelector.value;
}

function onSelectMonth(date: Date): void {
  shouldShowMonthsSelector.value = false;
  emit('select-month', date);
}
</script>

<template>
  <PeekPriceModalMobileLayout
    @cancel="emit('cancel')"
    @confirm="emit('confirm')"
    @layout-shift="onLayotShift"
  >
    <template #monthsSelector>
      <MonthsSelector
        :should-show-months-selector="shouldShowMonthsSelector"
        :months="props.calendar.months"
        :selected-month-iso-date="props.calendar.selectedMonth"
        button-font="font-label-l-bold"
        class="months-selector"
        @select-month="onSelectMonth"
        @toggle-months-selector="onToggleMonthsSelector"
      />
    </template>

    <template #priceCalendar>
      <PriceCalendar
        v-if="props.calendar.priceCalendarState.anyResultsLoaded()"
        :selected-month="props.calendar.selectedMonth"
        :months="props.calendar.months"
        :price-calendar-state="props.calendar.priceCalendarState"
        :allow-selection-for="[PriceCalendarTileType.CurrentMonthAvailableDay]"
        :should-show-header="false"
        @select-month="emit('select-month', $event)"
        @select-date="emit('select-date', $event)"
      />
    </template>

    <template #priceRibbon>
      <PriceCalendarSingleMonthRibbon
        v-if="props.calendar.priceCalendarState.anyResultsLoaded()"
        :selected-month="props.calendar.selectedMonth"
        :months="props.calendar.months"
        :price-calendar-state="props.calendar.priceCalendarState"
        :allow-selection-for="[PriceCalendarTileType.CurrentMonthAvailableDay]"
        @select-month="emit('select-month', $event)"
        @select-date="emit('select-date', $event)"
      />
      <AppFlicker
        v-else
        height="spacing-32"
        width="spacing-80"
        border-radius="border-radius-12"
      />
    </template>

    <template #itinerary>
      <PeekPriceModalItinerary :itinerary="props.itinerary" />
    </template>
  </PeekPriceModalMobileLayout>
</template>

<style scoped lang="scss">
.months-selector {
  height: 39px;
  z-index: 3;
}
</style>
